import { api } from './api';

/** One tool the agent ran for an answer, shown on the run timeline. */
export interface AgentStep {
  tool: string;
  label: string;
  refused: boolean;
  ms: number;
}

/** How the answer's figures were checked against Clarid's records (ASK_CLARID_PILOT.md §5). */
export type NumbersCheck = 'passed' | 'passed_on_retry' | 'fallback' | 'no_figures';

/** A card under an answer: an action the agent proposed, or a data fix, decided by a person's click. */
export type AgentCardRef = { kind: 'action' | 'data_fix' | 'engagement' | 'deliverable' | 'recommendations'; id: string };

export interface AgentTurn {
  conversationId: string;
  messageId: string;
  answer: string;
  steps: AgentStep[];
  cards: AgentCardRef[];
  numbersCheck: NumbersCheck;
}

export interface ActionCardState {
  kind: 'action';
  id: string;
  type: 'run_fld' | 'export_fld_word' | 'export_file_review' | 'export_client_report' | 'export_aa_maps' | 'export_board_deck' | 'record_recommendations' | 'produce' | 'load_file';
  title: string;
  status: 'PROPOSED' | 'RUNNING' | 'AWAITING_UPLOAD' | 'DONE' | 'FAILED' | 'REJECTED';
  /** For a load: the file and its SHA-256, and any warnings to check first. */
  params?: {
    fileType?: 'hmda_lar' | 'cra_loan_register' | 'branch_list'; fileName?: string; sha256?: string; year?: string | null; warnings?: string[];
    /** For a produced deliverable: which one, in words. */
    capability?: string; label?: string;
    /** For recording recommendations: exactly what will be recorded. */
    preview?: { title: string; detail: string | null; repeat: string | null }[];
  } | null;
  result: {
    analysisId?: string; downloadPath?: string; deliverableId?: string;
    validationId?: string; year?: string | null; totalLoans?: number; link?: string; rows?: number;
    /** For a client report: what the advisor still fills in by hand. */
    gaps?: string[];
    /** For recorded recommendations. */
    recorded?: number; repeats?: number; skipped?: number;
    /** What a produced file leaves out. */
    notes?: string[];
    /** After a client report: the card offering to track its recommendations. */
    followUpActionId?: string;
  } | null;
  error: string | null;
  decidedAt: string | null;
  analysis: { status: string; overallRiskLevel: string | null } | null;
}

export interface DataFixCardState {
  kind: 'data_fix';
  id: string;
  title: string;
  status: 'PROPOSED' | 'APPLIED' | 'REJECTED' | 'SUPERSEDED';
  changeCount: number;
  preview: {
    groups?: { field: string; before: string; after: string; count: number }[];
    notChangedCount?: number;
  } | null;
  appliedCount: number | null;
  decidedAt: string | null;
  decisionNote: string | null;
}

export interface EngagementItemState {
  id: string;
  ordinal: number;
  sowText: string;
  capability: string;
  capabilityName: string;
  trust: 'validated' | 'beta' | 'not_available' | 'advisor' | 'record' | 'bank';
  /** What the plan said. */
  state: 'READY' | 'NEEDS_DATA' | 'IN_CLARID' | 'ADVISOR' | 'BANK_SUPPLIES' | 'NOT_AVAILABLE';
  detail: string;
  /** What running it did. */
  status: 'PENDING' | 'RUNNING' | 'DELIVERING' | 'DONE' | 'FAILED' | 'SKIPPED';
  result: { downloadPath?: string; link?: string; analysisId?: string; deliverableId?: string } | null;
  error: string | null;
}

export interface EngagementCardState {
  kind: 'engagement';
  id: string;
  title: string;
  /** SOW, or a regulator's exam request letter answered with a response index. */
  engagementKind: 'SOW' | 'REQUEST_LETTER' | 'PLAYBOOK';
  regulator: string | null;
  dueDate: string | null;
  indexDeliverableId: string | null;
  sowFileName: string | null;
  status: 'PROPOSED' | 'RUNNING' | 'COMPLETE' | 'REJECTED';
  decidedAt: string | null;
  analysisId: string | null;
  items: EngagementItemState[];
}

export type SignoffRole = 'PREPARER' | 'REVIEWER' | 'APPROVER';

/** A stored deliverable and its sign-off (CLARID-SPEC-AGENT-001 §1A.9). */
export interface DeliverableCardState {
  kind: 'deliverable';
  id: string;
  fileName: string;
  sha256: string;
  status: 'DRAFT' | 'PREPARED' | 'REVIEWED' | 'APPROVED' | 'RETURNED';
  createdAt: string;
  analysisId: string | null;
  downloadPath: string;
  signoffs: { role: SignoffRole | 'RETURNED'; userName: string; signedAt: string; note: string | null }[];
}

export type CardState = ActionCardState | DataFixCardState | EngagementCardState | DeliverableCardState;

export interface AgentConversationSummary {
  id: string;
  title: string;
  updatedAt: string;
}

export interface AgentStoredMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  metadata?: { steps?: AgentStep[]; cards?: AgentCardRef[]; numbersCheck?: NumbersCheck; attachments?: { name: string; size: number }[] } | null;
  createdAt: string;
}

export const agentApi = {
  send: (teamId: string, data: { message: string; conversationId?: string | null; files: File[] }) => {
    const form = new FormData();
    form.append('message', data.message);
    if (data.conversationId) form.append('conversationId', data.conversationId);
    for (const f of data.files) form.append('files', f);
    return api.post<AgentTurn>(`/teams/${teamId}/agent/messages`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 180_000,
    });
  },

  home: (teamId: string) => api.get<{ home: AgentHome }>(`/teams/${teamId}/agent/home`),

  conversations: (teamId: string) =>
    api.get<{ conversations: AgentConversationSummary[]; available: boolean }>(`/teams/${teamId}/agent/conversations`),

  conversation: (teamId: string, id: string) =>
    api.get<{ conversation: { id: string; messages: AgentStoredMessage[] } }>(`/teams/${teamId}/agent/conversations/${id}`),

  cards: (teamId: string, refs: AgentCardRef[]) =>
    api.get<{ cards: CardState[] }>(`/teams/${teamId}/agent/cards`, {
      params: {
        actions: refs.filter((r) => r.kind === 'action').map((r) => r.id).join(','),
        fixes: refs.filter((r) => r.kind === 'data_fix').map((r) => r.id).join(','),
        engagements: refs.filter((r) => r.kind === 'engagement').map((r) => r.id).join(','),
        deliverables: refs.filter((r) => r.kind === 'deliverable').map((r) => r.id).join(','),
      },
    }),

  /** `body`: what the person filled in on the card (the cover letter for a client report). */
  approveAction: (teamId: string, id: string, body?: unknown) => api.post<{ card: ActionCardState }>(`/teams/${teamId}/agent/actions/${id}/approve`, body),
  /** After the browser sent an approved file to the HMDA upload: the server confirms by the file's hash. */
  confirmLoad: (teamId: string, id: string) => api.post<{ card: ActionCardState }>(`/teams/${teamId}/agent/actions/${id}/confirm-load`),
  rejectAction: (teamId: string, id: string) => api.post<{ card: ActionCardState }>(`/teams/${teamId}/agent/actions/${id}/reject`),

  approveEngagement: (teamId: string, id: string) => api.post<{ card: EngagementCardState }>(`/teams/${teamId}/agent/engagements/${id}/approve`),
  rejectEngagement: (teamId: string, id: string) => api.post<{ card: EngagementCardState }>(`/teams/${teamId}/agent/engagements/${id}/reject`),

  deliverable: (teamId: string, id: string) => api.get<{ card: DeliverableCardState }>(`/teams/${teamId}/agent/deliverables/${id}`),
  signDeliverable: (teamId: string, id: string, role: SignoffRole, note?: string) =>
    api.post<{ card: DeliverableCardState }>(`/teams/${teamId}/agent/deliverables/${id}/sign`, { role, note }),
  returnDeliverable: (teamId: string, id: string, note: string) =>
    api.post<{ card: DeliverableCardState }>(`/teams/${teamId}/agent/deliverables/${id}/return`, { note }),

  recommendations: (teamId: string, includeClosed: boolean) =>
    api.get<{ recommendations: RecommendationRow[] }>(`/teams/${teamId}/agent/recommendations`, { params: includeClosed ? { closed: '1' } : {} }),
  updateRecommendation: (teamId: string, id: string, body: { status?: RecommendationStatus; ownerName?: string; dueDate?: string | null; note?: string }) =>
    api.patch(`/teams/${teamId}/agent/recommendations/${id}`, body),

  connectors: (teamId: string) => api.get<ConnectorStatus>(`/teams/${teamId}/connectors`),
  startMicrosoft: (teamId: string) => api.post<{ url: string }>(`/teams/${teamId}/connectors/microsoft/start`),
  setMicrosoftFolder: (teamId: string, url: string) => api.post<ConnectorStatus>(`/teams/${teamId}/connectors/microsoft/folder`, { url }),
  disconnectMicrosoft: (teamId: string) => api.delete<ConnectorStatus>(`/teams/${teamId}/connectors/microsoft`),

  // Data fixes keep their own approval route (Owner or Compliance Officer).
  approveFix: (teamId: string, id: string, note?: string) => api.post(`/teams/${teamId}/data-fixes/${id}/approve`, { note }),
  rejectFix: (teamId: string, id: string, note?: string) => api.post(`/teams/${teamId}/data-fixes/${id}/reject`, { note }),

  /** Download a file the server produced (e.g. the FLD Word report with its evidence appendix). */
  download: async (path: string, fallbackName: string) => {
    const res = await api.get(path, { responseType: 'blob' });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement('a');
    a.href = url;
    const cd = (res.headers as Record<string, string | undefined>)['content-disposition'];
    a.download = cd?.match(/filename="?([^"]+)"?/)?.[1] || fallbackName;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },
};

/** SHA-256 of a file, as Clarid fingerprints it (hex). */
export async function fileSha256(file: File): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** The bank home view (in-bank): where this bank stands. */
export interface AgentHome {
  readiness: { ready: number; total: number; warnings: number; blocked: { capability: string; reasons: string[] }[] } | null;
  pendingFixes: number;
  awaitingSignoff: { id: string; fileName: string; status: string; createdAt: string }[];
  openPlans: { id: string; title: string; kind: string; status: string; proposedAt: string; conversationId: string | null }[];
  playbooks: { id: string; name: string; description: string }[];
  recommendations: { open: number; overdue: number; repeats: number } | null;
}

export type RecommendationStatus = 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'RISK_ACCEPTED';

/** One tracked recommendation. */
export interface RecommendationRow {
  id: string;
  source: 'FLD_FOCAL_POINT' | 'MANUAL';
  title: string;
  detail: string | null;
  status: RecommendationStatus;
  ownerName: string | null;
  dueDate: string | null;
  reviewYear: number | null;
  createdAt: string;
  closedAt: string | null;
  closeNote: string | null;
  overdue: boolean;
  repeatOf: { id: string; reviewYear: number | null; status: string } | null;
}

/** Connected storage (SharePoint / OneDrive): whether Clarid's connector is set up, and this bank's connection. */
export interface ConnectorStatus {
  microsoft: {
    configured: boolean;
    connection: {
      status: 'PENDING_FOLDER' | 'ACTIVE' | 'ERROR';
      accountName: string | null;
      folderName: string | null;
      folderWebUrl: string | null;
      connectedAt: string;
      lastError: string | null;
    } | null;
  };
}
